import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DeliveryTypeEnum } from '@/app/common/general-types/briefs.dto';
import { MbaIcon } from '../../assignment/atoms/mba-icon/mba-icon';
import { DELIVERY_OPTIONS, plural } from '../briefing.helpers';
import { BriefingService } from '../briefing.service';
import { bf } from '../briefing-ui';
import { BriefingDrawer } from '../molecules/briefing-drawer/briefing-drawer';
import { BriefingSection } from '../molecules/briefing-section/briefing-section';

/**
 * Product Delivery: none, coupon or ship. There is no stored coupon code: the brand shares it
 * with creators themselves, as before. The method locks once anyone is hired (the API agrees).
 */
@Component({
  selector: 'mbt-briefing-delivery',
  imports: [BriefingSection, BriefingDrawer, MbaIcon],
  template: `
    <mbt-briefing-section key="gift" icon="gift" title="Product Delivery" helper="How each hired creator gets the product."
      status="set" [flash]="flash()" (edit)="open()">
      <div class="flex items-center gap-3.5 rounded-lg border border-(--border-subtle) px-4 py-3.5">
        <span [class]="bf.iconTile"><mba-icon [name]="current().icon" size="size-5" /></span>
        <span class="flex min-w-0 flex-col gap-0.5">
          <b [class]="bf.display + ' text-[19px]'">{{ current().label }}</b>
          <span class="text-[13px] text-(--fg-3)">{{ current().description }}</span>
        </span>
      </div>
      @switch (current().type) {
        @case (DeliveryTypeEnum.Address_Required) {
          <div [class]="bf.info">
            <mba-icon name="truck" size="size-4" />
            <span class="flex-1">Creators share their address when you hire them. Track every parcel in Logistics.</span>
            <button type="button" [class]="bf.link" (click)="openLogistics.emit()">Open Logistics<mba-icon name="arrow-right" size="size-4" /></button>
          </div>
        }
        @case (DeliveryTypeEnum.Discount_Code) {
          <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>Share the coupon code with each creator you hire, for example in your hire message.</span></div>
        }
      }
    </mbt-briefing-section>

    @if (editing()) {
    <mbt-briefing-drawer icon="gift" title="Product Delivery" helper="How each hired creator gets the product."
      [saving]="saving()" [canSave]="!locked()" [note]="locked() ? 'Locked while creators are hired' : ''"
      (closed)="editing.set(false)" (save)="save()">
      <div [class]="bf.field" role="radiogroup" aria-labelledby="bf-gift-label">
        <span id="bf-gift-label" [class]="bf.fieldLabel">How creators get the product</span>
        @for (option of options; track option.type) {
          <button type="button" role="radio" [attr.aria-checked]="draft() === option.type" [disabled]="locked()"
            [class]="draft() === option.type ? bf.optTileOn : bf.optTile" (click)="draft.set(option.type)">
            <mba-icon [name]="option.icon" size="size-5" />
            <span class="flex flex-col gap-0.5">
              <b class="text-sm text-(--fg-1)">{{ option.label }}</b>
              <span class="text-xs text-(--fg-3)">{{ option.description }}</span>
            </span>
          </button>
        }
      </div>
      @if (locked()) {
        <div [class]="bf.infoLock"><mba-icon name="lock" size="size-4" />
          <span>The delivery method is locked because {{ hiredText() }} hired. Shipments and codes already promised stay as they are.</span></div>
      } @else if (draft() === DeliveryTypeEnum.Address_Required) {
        <div [class]="bf.info"><mba-icon name="truck" size="size-4" /><span>Creators share their address when you hire them. You’ll track parcels in Logistics.</span></div>
      } @else if (draft() === DeliveryTypeEnum.Discount_Code) {
        <div [class]="bf.info"><mba-icon name="info" size="size-4" /><span>You share the code with each creator you hire.</span></div>
      }
    </mbt-briefing-drawer>
    }
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingDelivery {
  protected readonly bf = bf;
  protected readonly DeliveryTypeEnum = DeliveryTypeEnum;
  protected readonly options = DELIVERY_OPTIONS;
  private readonly svc = inject(BriefingService);

  readonly flash = input(false);
  readonly openLogistics = output<void>();

  protected readonly current = computed(
    () => DELIVERY_OPTIONS.find((o) => o.type === Number(this.svc.brief()?.deliveryType)) ?? DELIVERY_OPTIONS[0],
  );
  protected readonly locked = computed(() => this.svc.hired() > 0);
  protected readonly hiredText = computed(() => {
    const n = this.svc.hired();
    return `${plural(n, 'creator')} ${n === 1 ? 'is' : 'are'}`;
  });

  protected readonly editing = signal(false);
  protected readonly saving = signal(false);
  protected readonly draft = signal<DeliveryTypeEnum>(DeliveryTypeEnum.none);

  open(): void {
    this.draft.set(this.current().type);
    this.editing.set(true);
  }

  protected async save(): Promise<void> {
    if (this.draft() === this.current().type) {
      this.editing.set(false);
      return;
    }
    this.saving.set(true);
    const ok = await this.svc.save([() => this.svc.delivery(this.draft())], 'Product Delivery saved');
    this.saving.set(false);
    if (ok) {
      this.editing.set(false);
    }
  }
}
